import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Avatar } from "component/shared/avatar";
import { Agent } from "model/agent";
import { useTheme } from "@mui/material/styles";
import * as Icons from "react-feather";
import { styles } from "./style";
import { RoleTag, RoutingTag, ROLE_TAG_LABELS } from "./member-meta";
import { Tag } from "component/shared/tag";

interface Props extends WithStyles<typeof styles> {
	agent: Agent;
	/** Name with the "(Account Owner)" / "(You)" suffixes already applied. */
	displayName: string;
	roleTags: RoleTag[];
	routingTags: RoutingTag[];
	leadCount?: number;
	billable: boolean;
	lastLogin?: string | null;
	bioUrl?: string;
}

const Field: React.FC<WithStyles<typeof styles> & { label: string }> = ({ classes = {}, label, children }) => (
	<div className={classes.memberCardRow}>
		<Mui.Typography className={classes.memberCardLabel}>{label}</Mui.Typography>
		<div>{children}</div>
	</div>
);

const MemberCardComponent: React.FC<Props> = ({
	agent,
	displayName,
	roleTags,
	routingTags,
	leadCount,
	billable,
	lastLogin,
	bioUrl,
	classes = {},
}) => {
	const theme = useTheme();
	return (
		<Mui.Paper variant="outlined" className={classes.memberCard}>
			<Mui.Grid container spacing={2} alignItems="center" className={classes.memberCardHeader}>
				<Mui.Grid item>
					<Avatar phrase={agent.label} src={agent.photoUrl} color={theme.palette.primary.main} />
				</Mui.Grid>
				<Mui.Grid item xs>
					<Mui.Typography variant="subtitle1">{displayName}</Mui.Typography>
					<Mui.Link href={`mailto:${agent.email}`} className={classes.email}>
						{agent.email}
					</Mui.Link>
				</Mui.Grid>
			</Mui.Grid>
			<Mui.Divider />
			<Field classes={classes} label="Member ID">
				<Mui.Typography>{agent.id}</Mui.Typography>
			</Field>
			<Field classes={classes} label="Role">
				<div className={classes.tagStack}>
					{roleTags.map((tag) => (
						<Tag key={tag} label={ROLE_TAG_LABELS[tag]} variant={tag === "Admin" ? "primary" : "brand"} />
					))}
				</div>
			</Field>
			<Field classes={classes} label="Billable">
				<Mui.Typography>{billable ? "Yes" : "No"}</Mui.Typography>
			</Field>
			<Field classes={classes} label="Leads">
				<Mui.Typography>{leadCount ?? 0}</Mui.Typography>
			</Field>
			<Field classes={classes} label="Active Listings">
				<Mui.Typography>{agent.activeListingsCount ?? 0}</Mui.Typography>
			</Field>
			<Field classes={classes} label="Office">
				<Mui.Typography>{agent.office?.name ?? ""}</Mui.Typography>
			</Field>
			<Field classes={classes} label="Routing">
				<div className={classes.tagStack}>
					{routingTags.map((tag) => (
						<Tag
							key={tag}
							label={tag}
							variant="neutral"
							icon={tag === "Round Robin" ? <Icons.RotateCw size={14} /> : <Icons.MapPin size={14} />}
						/>
					))}
				</div>
			</Field>
			<Field classes={classes} label="Last Login">
				<Mui.Typography>{lastLogin}</Mui.Typography>
			</Field>
			{bioUrl && (
				<div className={classes.memberCardBio}>
					<Mui.Link href={bioUrl} target="_blank" rel="noopener" className={classes.bioLink}>
						View Agent Bio
						<Icons.ExternalLink size={16} />
					</Mui.Link>
				</div>
			)}
		</Mui.Paper>
	);
};

export const MemberCard = withStyles(MemberCardComponent, styles);
